import { handleResponseErrors } from '@restDataAccess/handleResponseErrors';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import type { ApiNameType } from '@/constants';

import { authorizationHeader } from '../authorizationHeader.ts';
import { useClearCacheCallback } from './cacheHooks.ts';
import { assertApiBaseForFetch } from './getApiUrl/getApiUrl.functions.ts';
import { useGetApiUrl } from './useGetApiUrl.ts';

type updateProps = {
  baseApiRoute: ApiNameType;
  cacheKeys: string[];
  headers?: HeadersInit;
  id?: string;
  identifier?: string;
  putOrPatch?: 'PUT' | 'PATCH';
  route: string;
};

export const useAuthApiUpdate = <T, V = void>({
  baseApiRoute,
  cacheKeys,
  headers,
  id,
  identifier,
  putOrPatch = 'PUT',
  route,
}: updateProps) => {
  const auth = useAuth();
  const api = useGetApiUrl(baseApiRoute);
  const updateDevice = async (device: Partial<T>): Promise<V> => {
    try {
      const base = assertApiBaseForFetch(api);
      const response = await fetch(`${base}${route}${id ? `/${id}` : ''}`, {
        method: putOrPatch,
        body: JSON.stringify(device),
        headers: {
          ...authorizationHeader(auth.user),
          'Content-Type': 'application/json',
          ...headers,
        },
      });
      return await handleResponseErrors<V>(response, identifier);
    } catch (e) {
      console.error(`Failed to update device ${identifier}`, e);
      throw e;
    }
  };

  const clearCacheCallback = useClearCacheCallback(cacheKeys);

  return useMutation({
    mutationFn: updateDevice,
    onSuccess: () => clearCacheCallback(),
    onError: () => clearCacheCallback(),
  });
};
